import { useCallback, useEffect, useRef, useState } from "react";
import AskAddyV2 from "../../models/askAddyV2";

// The app catalog loads this many at a time, the next page as the end of the list scrolls into view.
const CATALOG_PAGE_SIZE = 10;
const NO_CATALOG = { apps: [], cursor: null, isLoading: true };

/**
 * One page of the app catalog for the search box's text, and the next pages as the list's end
 * (`endRef`, rendered only while `isEndWatched`) scrolls into view inside `scrollRef`. A new search
 * starts the list over.
 */
const useAppCatalog = (search, identity, scrollRef, isEndWatched) => {
  const [catalog, setCatalog] = useState(NO_CATALOG);
  const [error, setError] = useState("");
  const endRef = useRef(null);
  const requestRef = useRef(0);

  const loadPage = useCallback(
    async (cursor) => {
      const request = ++requestRef.current;
      setCatalog((current) => ({ ...current, isLoading: true }));
      try {
        // Browsing lists the catalog A to Z; a search keeps Composio's best matches first.
        const query = search.trim();
        const page = await AskAddyV2.integrations.list(
          { search: query, cursor, limit: CATALOG_PAGE_SIZE, ...(query ? {} : { sort: "alphabetical" }) },
          identity
        );
        if (request !== requestRef.current) {
          return;
        }
        setCatalog((current) => ({
          apps: cursor ? [...current.apps, ...page.apps.filter(({ app }) => !current.apps.some((listed) => listed.app === app))] : page.apps,
          cursor: page.cursor || null,
          isLoading: false,
        }));
      } catch (loadError) {
        if (request !== requestRef.current) {
          return;
        }
        setError(loadError.message);
        // The apps already listed stay; the end of the list doesn't retry a failing page forever.
        setCatalog((current) => ({ ...current, cursor: null, isLoading: false }));
      }
    },
    [search, identity]
  );

  // The first page follows the search box (debounced).
  useEffect(() => {
    setCatalog(NO_CATALOG);
    const timer = setTimeout(() => loadPage(null), search ? 300 : 0);

    return () => clearTimeout(timer);
  }, [search, loadPage]);

  const { cursor, isLoading } = catalog;
  useEffect(() => {
    const end = endRef.current;
    if (!isEndWatched || !end || !cursor || isLoading) {
      return undefined;
    }

    // Watched inside the page's own scroll box, which is what the loan officer scrolls.
    const observer = new IntersectionObserver(([entry]) => entry.isIntersecting && loadPage(cursor), {
      root: scrollRef.current,
      rootMargin: "0px 0px 120px 0px",
    });
    observer.observe(end);

    return () => observer.disconnect();
  }, [isEndWatched, cursor, isLoading, loadPage, scrollRef]);

  return { catalog, error, endRef };
};

export default useAppCatalog;
